import { useEffect } from 'react';
import { useLanguage } from '../hooks/useLanguage';

/**
 * @component BrandTheme
 * @description Pushes the dictionary's brand colours into CSS variables.
 *
 *   The palette is declared in styles/index.css with the committed defaults so
 *   first paint is correct and a dictionary outage changes nothing. This
 *   component overwrites those variables once branding loads.
 *
 *   Only PRIMARY and SECONDARY are authored. The dark / tint / border / alpha
 *   steps are derived from PRIMARY, because they are all shades of the same
 *   colour — if an operator sets PRIMARY to blue, a hardcoded orange hover
 *   state would be worse than no theming at all. The derivation factors were
 *   fitted to reproduce the existing palette: with PRIMARY at #EF4123 they
 *   land within a couple of units of the committed #C7341D / #FEF1ED /
 *   #F8C7B5, so switching this on is visually a no-op.
 */

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** Parse #rgb or #rrggbb into [r,g,b]; null when the value isn't a hex colour. */
const toRgb = (value) => {
  const raw = String(value || '').trim();
  if (!HEX.test(raw)) return null;
  let hex = raw.replace('#', '');
  if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
  const n = parseInt(hex, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const toHex = ([r, g, b]) => '#' + [r, g, b]
  .map((c) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, '0'))
  .join('');

/** Multiply toward black. 0.82 reproduces #C7341D from #EF4123. */
const darken = (rgb, factor) => rgb.map((c) => c * factor);

/** Mix toward white. `amount` is how much white, 0–1. */
const tint = (rgb, amount) => rgb.map((c) => c + (255 - c) * amount);

const rgba = ([r, g, b], alpha) => `rgb(${Math.round(r)} ${Math.round(g)} ${Math.round(b)} / ${alpha})`;

/**
 * Space-separated channels, the shape Tailwind's
 * `rgb(var(--x) / <alpha-value>)` needs so opacity modifiers keep working.
 */
const triplet = (rgb) => rgb.map(Math.round).join(' ');

/** Blend `amount` of `over` on top of `base` (0-1). */
const mix = (base, over, amount) => base.map((c, i) => c + (over[i] - c) * amount);

export default function BrandTheme() {
  const brand = useLanguage('branding');

  useEffect(() => {
    const colors = brand?.COLORS;
    if (!colors) return;

    const root = document.documentElement;
    const primary = toRgb(colors.PRIMARY);

    if (primary) {
      root.style.setProperty('--moksy-brand', toHex(primary));
      root.style.setProperty('--moksy-brand-rgb', triplet(primary));
      root.style.setProperty('--moksy-brand-dark', toHex(darken(primary, 0.82)));
      root.style.setProperty('--moksy-brand-tint', toHex(tint(primary, 0.94)));
      root.style.setProperty('--moksy-brand-border', toHex(tint(primary, 0.70)));
      root.style.setProperty('--moksy-brand-a08', rgba(primary, 0.08));
      root.style.setProperty('--moksy-brand-a12', rgba(primary, 0.12));
    }

    // The remaining three feed the Tailwind theme (bg-background, text-neutral,
    // text-accent and friends) rather than the brand-orange family.
    const secondary = toRgb(colors.SECONDARY);
    if (secondary) {
      root.style.setProperty('--moksy-secondary', toHex(secondary));
      root.style.setProperty('--moksy-secondary-rgb', triplet(secondary));
    }

    const accent = toRgb(colors.ACCENT);
    if (accent) root.style.setProperty('--moksy-accent-rgb', triplet(accent));

    const background = toRgb(colors.BACKGROUND);
    if (background) {
      root.style.setProperty('--moksy-background-rgb', triplet(background));
      root.style.setProperty('--moksy-background', toHex(background));
    }

    const text = toRgb(colors.TEXT);
    if (text) {
      root.style.setProperty('--moksy-text-rgb', triplet(text));
      root.style.setProperty('--moksy-text', toHex(text));
    }

    // Surface ramp. The percentages were fitted against the committed
    // #FAFBFC / #F5F7FA / #F1F3F6 / #E5E7EB and land within ~3 units per
    // channel at the default colours, so switching this on changes nothing.
    if (background && text) {
      root.style.setProperty('--moksy-canvas', toHex(mix(background, text, 0.02)));
      root.style.setProperty('--moksy-soft', toHex(mix(background, text, 0.04)));
      root.style.setProperty('--moksy-divider', toHex(mix(background, text, 0.055)));
      root.style.setProperty('--moksy-border', toHex(mix(background, text, 0.11)));
    }
  }, [brand]);

  return null;
}
